<template>
  <main class="">
    <section
      class="main h-full mt-20 p-4 py-20 bg-white/50 bg-blend-lighten dark:bg-black/50 bg-[url('@/images/system/background.jpg')] dark:bg-[url('@/images/system/background-dark.jpg')]"
    >
      <div class="text-center">
        <h1 class="gradient-title">Effortless Image Editing and Conversion</h1>
      </div>

      <div class="text-center mt-4">
        <p class="text-lg lg:text-2xl text-heading">Edit Your Photos Online in Just a Few Clicks</p>
      </div>

      <AdSenseTop class="mt-8" />

      <div class="mt-8 mx-auto max-w-screen-xl">
        <h2 class="text-xl lg:text-3xl text-heading">AI Image Editor</h2>
        <div class="mt-6 grid grid-cols-12 gap-4">
          <FeatureItem
            v-for="feature in featureStore.popularFeatures"
            :key="feature.id"
            :feature="feature"
            modern
            class="col-span-12 sm:col-span-6 md:col-span-4 lg:col-span-3"
          />
        </div>
      </div>

      <div class="mt-8 mx-auto max-w-screen-xl">
        <h2 class="text-xl lg:text-3xl text-heading">More Tools</h2>
        <div class="mt-6 grid grid-cols-12 gap-4">
          <FeatureItem
            v-for="feature in featureStore.otherFeatures"
            :key="feature.id"
            :feature="feature"
            class="col-span-12 sm:col-span-6 md:col-span-4 lg:col-span-3"
          />
        </div>
      </div>
    </section>

    <section class="site-specific mx-auto max-w-screen-xl px-2">
      <div class="text-center p-12">
        <h2 class="text-heading">Photo Editing Made Simple and Powerful</h2>
      </div>
      <div class="grid grid-cols-1 md:grid-cols-3 gap-16 px-8">
        <div>
          <h3 class="text-heading">Optimize Images for the Web</h3>
          <p class="text-normal">
            Effortlessly resize, compress, and transform your photos with pixel-perfect precision. Perfectly tailor your
            images for web, social media, and e-commerce platforms. Get instant results with our user-friendly tools
            that ensure your visuals stand out online.
          </p>
        </div>

        <div>
          <h3 class="text-heading">Batch Photo Processing</h3>
          <p class="text-normal">
            Streamline your workflow with batch processing capabilities. PicEditor supports a wide range of formats
            including PNG, JPG, GIF, WEBP, and HEIC. Easily convert and process multiple files simultaneously.
          </p>
        </div>

        <div>
          <h3 class="text-heading">Trusted Online Image Editor</h3>
          <p class="text-normal">
            PicEditor is the go-to solution for image editing online. Embrace the simplicity and efficiency of our
            platform, trusted and loved by users worldwide. Secure, reliable, and easy to use — enhance your images and
            documents directly from your browser with total peace of mind.
          </p>
        </div>
      </div>
    </section>

    <section class="site-specific bg-emerald-100 mt-12 dark:bg-emerald-900">
      <div class="site-specific mx-auto max-w-screen-xl px-2">
        <div class="text-center pt-12 pb-16">
          <img src="@/images/icons/secure.png" alt="Security and Reliability" class="mx-auto w-24 mb-6" />
          <h2 class="text-heading">Security and Reliability</h2>
          <p class="text-normal">
            Committed to your privacy and security, ensuring a secure connection with HTTPS for all operations.
          </p>
        </div>
      </div>
    </section>
  </main>
</template>

<script setup lang="ts">
  import { useStore } from '$/stores'
  import { useFeatureStore } from '$/stores/feature'
  import FeatureItem from '&/FeatureItem.vue'
  import { useHead } from '@unhead/vue'
  import AdSenseTop from '&/adsense/AdSenseTop.vue'

  const store = useStore()
  const featureStore = useFeatureStore()

  useHead({
    title: store.systemConfigs.home_meta_title,
    meta: [
      {
        name: 'description',
        content: `${store.systemConfigs.home_meta_description}`,
      },
      {
        name: 'og:title',
        content: `${store.systemConfigs.home_meta_title}`,
      },
      {
        name: 'og:description',
        content: `${store.systemConfigs.home_meta_description}`,
      },
    ],
  })
</script>

<style scoped lang="scss">
  .site-specific {
    h2 {
      font-weight: bolder;
      font-size: 1.75rem;
      color: #3f3f46;
    }
    h3 {
      font-weight: bolder;
      font-size: 1.2rem;
      color: #334155;
    }
    p {
      color: #475569;
      margin-top: 0.75rem;
    }
  }
</style>
